import { DialogCheckBox } from 'components/DialogCheckBox';
import { DialogConfirmation } from 'components/DialogConfirmation';
import { Snackbar } from 'components/Snackbar';
import { IUseDisclose, useDisclose, useLoader } from 'hooks/utilHooks';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { EmployeeService } from 'services';
import { IEmployee, IEmployeeCreate, IEmployeeList } from 'types/Employee';

import { AlertColor } from '@mui/material';

export interface IEmployeesContext {
  page: number;
  setPage: Function;
  maxPage: number;
  getEmployees: Function;
  getEmployee: Function;
  getEmployeesCb: Function;
  handleOnSubmit: Function;
  handleSubmitWorksheet: Function;
  setFileRegisterEmployee: Function;
  deleteDialog: IUseDisclose;
  loadingDialog: IUseDisclose;
  employee: IEmployee | undefined;
  employees: IEmployeeList;
  listErrors: IListErrors[];
  query: string;
  setQuery: Function;
  setListErrors: Function;
  fileRegisterEmployee: File | null | undefined;
  loadingDelete: any;
  loadingUpdate: boolean;
  loadingList: boolean;
  loadingCreate: boolean;
  searchEmployees: (filter: string) => void;
  selectEmployee: (id: string) => void;
  selectedEmployees: { [key: string]: string };
}

interface ISnackbar {
  message: string | null;
  variant?: AlertColor | undefined;
}

type IListErrors = {
  message: string;
  line: number;
};

export const EmployeesContext = createContext<IEmployeesContext>(
  {} as IEmployeesContext
);

type Props = {
  children: ReactNode;
};

export const EmployeesProvider = ({ children }: Props) => {
  const [employees, setEmployees] = useState<IEmployeeList>({
    count: 0,
    list: [],
    totalPages: 1,
  });

  const [selectedEmployees, setSelectedEmployees] = useState(
    {} as IEmployeesContext['selectedEmployees']
  );

  const [fileRegisterEmployee, setFileRegisterEmployee] =
    useState<File | null>();

  const [listErrors, setListErrors] = useState<IListErrors[]>([]);

  const [snackbar, setSnackbar] = useState<ISnackbar>({
    message: '',
    variant: 'info',
  });

  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [maxPage, setMaxPage] = useState(1);

  const loadingList = useLoader(true);
  const loadingCreate = useLoader();
  const loadingUpdate = useLoader();
  const loadingDelete = useLoader();

  const deleteDialog = useDisclose();
  const successModal = useDisclose();
  const loadingDialog = useDisclose();

  const [inPage, setInPage] = useState(page);

  const [employee] = useState<IEmployee>();

  const getEmployees = useCallback(async (page: number, filter: string) => {
    try {
      const employees = await EmployeeService.getEmployeeList(page, 10, filter);

      return employees;
    } catch (error) {
      setMaxPage(1);
      setSnackbar({
        message: 'Não foi possível listar os colaboradores',
        variant: 'error',
      });
      return { list: [], count: 1, totalPages: 1 };
    }
  }, []);

  const getEmployeesCb = useCallback(
    async (force = false) => {
      if (inPage !== page || force === true) {
        const employees = await getEmployees(page, query);

        setEmployees(employees);
        setMaxPage(employees.totalPages);
      }
    },
    [inPage, page, getEmployees, query]
  );

  const getEmployee = useCallback(
    async (id: string) => {
      if (!id) {
        return;
      }

      try {
        loadingUpdate.showLoader();
      } catch (reason) {
      } finally {
        loadingUpdate.hideLoader();
      }
    },
    [loadingUpdate]
  );

  const handleOnSubmit = async (values: IEmployeeCreate & { id?: string }) => {
    try {
      loadingCreate.showLoader();

      if (values.id) {
        await EmployeeService.updateEmployee({
          id: values.id,
          email: values.email,
          phone: values.phone,
        });

        setSnackbar({
          message: 'Colaborador atualizado com sucesso!',
          variant: 'success',
        });
      } else {
        await EmployeeService.createEmployee(values);

        setSnackbar({
          message: 'Colaborador cadastrado com sucesso!',
          variant: 'success',
        });
      }

      getEmployeesCb(true);
      return true;
    } catch (error) {
      const { message } = error as Error;

      setSnackbar({
        message,
        variant: 'error',
      });

      return false;
    } finally {
      loadingCreate.hideLoader();
    }
  };

  const handleSubmitWorksheet = async (value: File | null | undefined) => {
    try {
      loadingCreate.showLoader();
      loadingDialog.onOpen();

      await EmployeeService.createGroupEmployeeWorksheet(value as File);

      getEmployeesCb(true);

      setSnackbar({
        message: 'Colaboradores cadastrados com sucesso!',
        variant: 'success',
      });
    } catch (error) {
      if (Array.isArray(error)) {
        setListErrors(error);
      } else {
        const { message } = error as Error;
        setSnackbar({
          message,
          variant: 'error',
        });
      }

      setSnackbar({
        message:
          'Falha no cadastro. Clique em "Importar" para conferir os erros.',
        variant: 'error',
      });
    } finally {
      loadingCreate.hideLoader();
      loadingDialog.onClose();
      setFileRegisterEmployee(null);
    }
  };

  const deleteEmployee = async () => {
    try {
      loadingDelete.showLoader();
      await EmployeeService.deleteEmployeeById(
        Object.values(selectedEmployees)
      );

      deleteDialog.onClose();
      successModal.onOpen();

      setSelectedEmployees({});

      getEmployeesCb(true);

      setSnackbar({
        message: 'Colaborador(es) apagados com sucesso',
        variant: 'success',
      });
    } catch (error) {
      setSnackbar({
        message: 'Não foi possível apagar o(s) colaborador(es)',
        variant: 'error',
      });
    } finally {
      loadingDelete.hideLoader();
    }
  };

  const searchEmployees = async (filter: string) => {
    try {
      loadingList.showLoader();
      const result = await getEmployees(1, filter);
      setEmployees(result);
      setMaxPage(result.totalPages);
    } catch (reason) {
      setSnackbar({
        message: 'Não foi possível listar os colaboradores',
        variant: 'error',
      });
    } finally {
      loadingList.hideLoader();
    }
  };

  const selectEmployee = (id: string) => {
    const newSelection = { ...selectedEmployees };

    if (newSelection[id]) {
      delete newSelection[id];
    } else {
      newSelection[id] = id;
    }

    setSelectedEmployees(newSelection);
  };

  useEffect(() => {
    getEmployees(1, '').then((employees: any) => {
      loadingList.hideLoader();
      setEmployees(employees);
      setMaxPage(employees.totalPages);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setEmployees, getEmployees]);

  useEffect(() => {
    setInPage(page);
  }, [page, setInPage, getEmployeesCb]);

  useEffect(() => {
    getEmployeesCb();
  }, [setInPage, getEmployeesCb]);

  return (
    <EmployeesContext.Provider
      value={{
        page,
        setPage,
        maxPage,
        getEmployees,
        getEmployeesCb,
        getEmployee,
        handleOnSubmit,
        handleSubmitWorksheet,
        setFileRegisterEmployee,
        deleteDialog,
        loadingDialog,
        employee,
        employees,
        listErrors,
        query,
        setQuery,
        setListErrors,
        fileRegisterEmployee,
        loadingDelete,
        loadingUpdate: loadingUpdate.loading,
        loadingList: loadingList.loading,
        loadingCreate: loadingCreate.loading,
        searchEmployees,
        selectEmployee,
        selectedEmployees,
      }}
    >
      {children}

      <Snackbar
        text={snackbar.message}
        snackbarOpen={Boolean(snackbar.message)}
        setSnackbarOpen={() => {
          setSnackbar({
            message: '',
          });
        }}
        severityType={snackbar.variant}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      />
      <DialogCheckBox
        title="Exclusão permantente do colaborador"
        text="Essa ação é permanente, você deseja continuar e excluir todos
        os dados desse colaborador?"
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        loadingDelete={loadingDelete.loading}
        deleteItem={deleteEmployee}
      />
      <DialogConfirmation
        title="Exclusão permantente do colaborador"
        text="Exclusão realizada com sucesso!"
        isOpen={successModal.isOpen}
        onClose={successModal.onClose}
      />
    </EmployeesContext.Provider>
  );
};
